import { useSelector } from "react-redux";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useEffect, useState } from "react";
import { actionGetPersonas, setCurrentPersona } from "@/store/slices/appSlice";
import { markChatAsRead } from "@/store/slices/chatSlice";
import { Chat } from "@/components/Chat";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import type { Persona } from "@/types/personas";
import type { ChatHistory } from "@/types/chat";
import { selectChats } from "@/store/selectors/chat";
import {
  selectPersonas,
  selectIsLoading,
  selectCurrentPersona,
} from "@/store/selectors/app";

export function Home() {
  const dispatch = useAppDispatch();
  const personas = useSelector(selectPersonas);
  const chats = useSelector(selectChats);
  const isLoading = useSelector(selectIsLoading);
  const currentPersona = useSelector(selectCurrentPersona);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    // Dispatch the async thunk to load the personas
    // Type cast to resolve the TypeScript problem
    dispatch(actionGetPersonas());
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-lg gap-2">
        <svg className="animate-spin h-6 w-6 text-blue-500" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        Loading...
      </div>
    );
  }

  // Filter personas based on the selected filter and sort alphabetically
  const filteredPersonas = personas
    .filter((persona: Persona) => {
      if (filter === "all") return true;
      if (filter === "aware") return persona.aware === true;
      if (filter === "not-aware") return persona.aware === false;
      return true;
    })
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const handlePersonaClick = (personaName: string) => {
    const persona = personas.find((p: Persona) => p.name === personaName);
    if (persona) {
      dispatch(setCurrentPersona(persona));
    } else {
      console.error("Persona not found");
    }

    // Mark the chat as read when it is selected
    const chat = chats.find((c: ChatHistory) => c.personaName === personaName);
    if (chat && chat.unreadCount > 0) {
      dispatch(markChatAsRead(personaName));
    }
  };

  // Get the number of unread messages for a persona
  const getUnreadCount = (personaName: string) => {
    const chat = chats.find((c: ChatHistory) => c.personaName === personaName);
    return chat?.unreadCount || 0;
  };

  return (
    <main className="h-full bg-gray-800">
      <div className="max-w-7xl mx-auto h-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 h-full">
          {/* Left column - 1/3 */}
          <div className="bg-gray-700 rounded-lg shadow-lg p-2 overflow-auto md:col-span-1 h-full flex flex-col">
            <div className="mb-6 p-4">
              <RadioGroup
                defaultValue="all"
                className="flex space-x-4"
                value={filter}
                onValueChange={setFilter}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="all"
                    id="all"
                    className="text-amber-400"
                  />
                  <Label htmlFor="all" className="text-gray-200">
                    All
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="aware"
                    id="aware"
                    className="text-amber-400"
                  />
                  <Label htmlFor="aware" className="text-gray-200">
                    Aware
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="not-aware"
                    id="not-aware"
                    className="text-amber-400"
                  />
                  <Label htmlFor="not-aware" className="text-gray-200">
                    Not aware
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <h2 className="text-2xl font-bold mb-4 p-2 text-gray-100">
              Personas
            </h2>
            <div className="space-y-4">
              {filteredPersonas.map((persona: Persona) => {
                const unreadCount = getUnreadCount(persona.name);
                return (
                  <div
                    key={persona.name}
                    className={`flex items-center space-x-4 p-4 bg-gray-600 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer ${
                      currentPersona?.name === persona.name
                        ? "ring-2 ring-amber-400 bg-gray-500"
                        : ""
                    }`}
                    onClick={() => handlePersonaClick(persona.name)}
                  >
                    <div className="flex-1">
                      <h3 className="text-lg font-bold flex items-center gap-2 text-gray-100">
                        {persona.name}
                        {unreadCount > 0 && (
                          <Badge variant="notification" className="text-xs">
                            {unreadCount}
                          </Badge>
                        )}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right column - 2/3 */}
          <div className="bg-gray-700 rounded-lg shadow-lg h-full md:col-span-2 flex flex-col overflow-auto">
            <Chat
              personaId={currentPersona?.id || ""}
              personaName={currentPersona?.name || ""}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
